import { useEffect, useMemo, useState } from "react";
import type { Product } from "@/data/product-types";
import { ProductCard, RequestSimilarLink } from "./product-card";
import { track } from "@/lib/analytics";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 12;

export interface FilterDef {
  id: string;
  label: string;
  match: (p: Product) => boolean;
}

export const commonFilters: FilterDef[] = [
  { id: "pens", label: "Pens", match: (p) => p.category === "pens" },
  { id: "gifts", label: "Gifts", match: (p) => p.category === "lathe-gifts" },
  { id: "cnc", label: "CNC", match: (p) => p.category === "cnc" },
  { id: "signs", label: "Signs", match: (p) => p.subcategory === "Signs" },
  { id: "available", label: "Available", match: (p) => p.status === "available" },
  {
    id: "previously-created",
    label: "Previously Created",
    match: (p) => p.status === "previously-created",
  },
];

export function ProductGrid({
  products,
  columns = 3,
  showRequestSimilar = true,
  eagerCount = 0,
  pageSize = PAGE_SIZE,
}: {
  products: Product[];
  columns?: 2 | 3 | 4;
  showRequestSimilar?: boolean;
  eagerCount?: number;
  pageSize?: number;
}) {
  const [shown, setShown] = useState(pageSize);

  useEffect(() => {
    setShown(pageSize);
  }, [products, pageSize]);

  if (!products.length) {
    return (
      <div className="surface-card mt-10 p-10 text-center">
        <h3 className="display-md">Nothing matches that combination yet</h3>
        <p className="lede mx-auto mt-3">
          Clear a filter to see more, or describe what you are looking for and Paul will tell you what is
          possible.
        </p>
      </div>
    );
  }

  const visible = products.slice(0, shown);

  return (
    <>
      <ul
        className={cn(
          "mt-10 grid gap-6 sm:grid-cols-2",
          columns === 3 && "lg:grid-cols-3",
          columns === 4 && "lg:grid-cols-4",
        )}
      >
        {visible.map((p, i) => (
          <li key={p.id} className="flex flex-col gap-3">
            <ProductCard product={p} eager={i < eagerCount} />
            {showRequestSimilar && p.status === "previously-created" && (
              <RequestSimilarLink product={p} className="h-11 w-full" />
            )}
          </li>
        ))}
      </ul>
      {shown < products.length && (
        <div className="mt-8 flex flex-col items-center gap-3">
          <p className="text-sm text-muted-foreground">
            Showing {shown} of {products.length} creations
          </p>
          <button
            type="button"
            onClick={() => setShown((n) => Math.min(n + pageSize, products.length))}
            className="tap-safe rounded-sm border border-ink/25 px-6 text-sm font-semibold hover:border-ink hover:bg-paper motion-craft transition-colors"
          >
            Show more creations
          </button>
        </div>
      )}
    </>
  );
}

export function FilterableGrid({
  source,
  filters = commonFilters,
  columns = 3,
  context = "gallery",
  initialFilter,
}: {
  source: Product[];
  filters?: FilterDef[];
  columns?: 2 | 3 | 4;
  context?: string;
  initialFilter?: string;
}) {
  const [active, setActive] = useState<string[]>(initialFilter ? [initialFilter] : []);

  const visible = useMemo(() => {
    if (!active.length) return source;
    const defs = filters.filter((f) => active.includes(f.id));
    return source.filter((p) => defs.every((f) => f.match(p)));
  }, [active, filters, source]);

  const toggle = (id: string) => {
    setActive((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      track("filter_products", { context, filters: next.join(",") || "none" });
      return next;
    });
  };

  return (
    <div>
      <div className="mt-8 flex flex-wrap items-center gap-2" role="group" aria-label="Filter creations">
        {filters.map((f) => {
          const on = active.includes(f.id);
          return (
            <button
              key={f.id}
              type="button"
              aria-pressed={on}
              onClick={() => toggle(f.id)}
              className={cn(
                "tap-safe rounded-full border px-4 text-sm font-medium motion-craft transition-colors",
                on
                  ? "border-forest bg-forest text-primary-foreground"
                  : "border-ink/20 text-ink/80 hover:border-ink hover:bg-paper",
              )}
            >
              {f.label}
              {on && <span className="sr-only"> (active filter)</span>}
            </button>
          );
        })}
        {active.length > 0 && (
          <button
            type="button"
            onClick={() => setActive([])}
            className="tap-safe px-3 text-sm font-semibold text-walnut underline underline-offset-4"
          >
            Clear filters
          </button>
        )}
      </div>
      <p aria-live="polite" className="mt-4 text-sm text-muted-foreground">
        {visible.length} {visible.length === 1 ? "creation" : "creations"}
        {active.length > 0 ? " match these filters" : ""}
      </p>
      <ProductGrid products={visible} columns={columns} />
    </div>
  );
}
